// TODO(M14): Delete this helper once the backfill migration
// (2026-05-26-multi-prices-per-product) is verified in production and
// every product has a non-empty prices[] array. See
// docs/superpowers/specs/2026-05-26-multi-prices-per-product-design.md
// section "Migration" + the runbook in
// apps/api/migrations/2026-05-26-multi-prices-per-product.runbook.md

/**
 * Frontend mirror of apps/api/src/products/prices/synthesizePriceFromLegacy.ts.
 *
 * When a legacy product has an empty prices[], synthesise a single price entry
 * from the flat product fields so the PricesList UI has something to show.
 * Emits the NESTED billing shape (recurring XOR paymentPlan — the same shape a
 * saved post-cutover price has); getFormValues flattens it into the Formik
 * draft fields through the same getPriceBillingConfig path as saved prices.
 *
 * The synthetic _id is the product's own _id string — this produces the same
 * deterministic id as the backend helper (which uses the first 12 hex bytes of
 * the product ObjectId). The migration runbook describes how these synthetic ids
 * are resolved to real price _ids once the backfill has run.
 *
 * @param {object} product  Raw product document from the API (amounts in cents).
 * @returns {object}        A single PriceLike subdoc with amounts already
 *                          converted to decimal-dollar strings for Formik.
 */
export function synthesizePriceFromLegacy(product) {
  const billingType = product.type === 'subscription' ? 'recurring' : 'one_time';
  const amount = product.pricingType === 'multiple' ? 0 : (product.price ?? 0);

  // Convert cents → decimal-dollar string so Formik text inputs render correctly.
  const currency = product.currency || 'usd';
  const toDollars = (cents) => {
    if (cents == null || cents === '' || cents === 0) return '';
    const isZeroDecimal = ['jpy', 'clp'].includes(currency.toLowerCase());
    return isZeroDecimal ? String(cents) : String(cents / 100);
  };

  const synth = {
    // Use the product _id as the synthetic price _id.
    // Backend helper uses mongoose.Types.ObjectId(hex), which for a 24-char hex
    // string is identity. This keeps the id stable across re-renders.
    _id: product._id ? String(product._id) : undefined,
    enabled: true,
    hasBeenPurchased: false,
    billingType,
    amount: toDollars(amount),
    currency,
    discountedFromPrice: (() => {
      if (product.discountedFromPrice == null || product.discountedFromPrice === '') return '';
      const n = Number(product.discountedFromPrice);
      return Number.isFinite(n) ? toDollars(n) : '';
    })(),
    payWhatYouWant: !!product.payWhatYouWant,
    pwywSuggestedPrice: '',
    setupFee: product.setupFee ? toDollars(product.setupFee) : '',
    setupFeeMultipliesWithQuantity: !!product.setupFeeMultipliesWithQuantity,
    stripePriceId: product.planId ?? undefined,
    stripePriceLivemode: product.planLivemode ?? true,
    label: '',
  };

  if (billingType === 'recurring') {
    const config = {
      interval: product.interval || 'month',
      intervalCount: product.intervalCount ?? 1,
      trialPeriodDays: product.trialPeriodDays ?? null,
      billingCycleAnchorConfig: product.billingCycleAnchorConfig
        ? {
            enabled: !!product.billingCycleAnchorConfig.enabled,
            dayOfMonth: product.billingCycleAnchorConfig.dayOfMonth ?? 1,
            month: product.billingCycleAnchorConfig.month ?? null,
          }
        : null,
      startDate: product.startDate ?? null,
    };
    if (product.planIterations > 0) {
      synth.paymentPlan = { ...config, planIterations: product.planIterations };
    } else {
      synth.recurring = config;
    }
  }

  return synth;
}
